import { createContext, useCallback, useContext, useEffect, useState } from "react";
import API from "../config/api.js";

// ─────────────────────────────────────────────────────────────────────────────
// Cookie helpers
// ─────────────────────────────────────────────────────────────────────────────
function setCookie(name, value, days = 30) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires};path=/`;
}
function getCookie(name) {
  return decodeURIComponent(
    document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))?.[1] ?? ""
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Round minutes to nearest 10
// ─────────────────────────────────────────────────────────────────────────────
function roundMinutes(m) {
  return [0, 10, 20, 30, 40, 50].reduce((prev, curr) =>
    Math.abs(curr - m) < Math.abs(prev - m) ? curr : prev
  );
}

function padTwo(n) {
  return String(n).padStart(2, "0");
}

function nowDefaults() {
  const d = new Date();
  return {
    userdate:   `${d.getFullYear()}-${padTwo(d.getMonth() + 1)}-${padTwo(d.getDate())}`,
    userhour:   padTwo(d.getHours()),
    userminute: padTwo(roundMinutes(d.getMinutes())),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Default state
// ─────────────────────────────────────────────────────────────────────────────
function defaultState() {
  const d = nowDefaults();
  return {
    open:             false,
    iddraft:          "",
    items:            [],       // [{ type, id, url, role }]
    idimage:          "",
    idimagecover:     "",
    idcaption:        "",
    idreel:           "",
    reel_image:       "",
    reel_file:        "",
    reel_filewithmusic: "",
    reel_url:         "",
    reel_music:       "",
    type:             "",
    content:          "",
    facebook:         false,
    instagram:        false,
    linkedin:         false,
    tiktok:           false,
    scheduled:        false,
    userdatetime:     "",
    userdate:         d.userdate,
    userhour:         d.userhour,
    userminute:       d.userminute,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Context
// ─────────────────────────────────────────────────────────────────────────────
const DraftPromptContext = createContext(null);

export function DraftPromptProvider({ children }) {
  const [state, setState] = useState(defaultState);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // ── Derived userdatetime ──────────────────────────────────────────────────
  const buildUserDatetime = useCallback((s = state) => {
    return `${s.userdate}T${s.userhour}:${s.userminute}`;
  }, [state]);

  // ── Patch state helper ────────────────────────────────────────────────────
  const patch = useCallback((updates) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  // ── Persist to cookie ─────────────────────────────────────────────────────
  const persistCookie = useCallback((s) => {
    const toSave = { ...s };
    const itemsVal = JSON.stringify(s.items || []);
    delete toSave.items;
    if (s.content) {
      toSave.content = btoa(unescape(encodeURIComponent(s.content)));
    }
    setCookie("_draft", JSON.stringify(toSave));
    setCookie("_draft_items", itemsVal);
  }, []);

  // ── Load from cookie ──────────────────────────────────────────────────────
  const loadFromCookie = useCallback(() => {
    const raw = getCookie("_draft");
    const rawItems = getCookie("_draft_items");
    if (!raw) return;
    try {
      const saved = JSON.parse(raw);
      if (saved.content) {
        try { saved.content = decodeURIComponent(escape(atob(saved.content))); } catch (_) {}
      }
      if (rawItems) {
        try { saved.items = JSON.parse(rawItems); } catch (_) { saved.items = []; }
      }
      setState((prev) => ({ ...prev, ...saved }));
      if (saved.open === true || saved.open === "true") setOpen(true);
    } catch (_) {}
  }, []);

  // Load on mount
  useEffect(() => { loadFromCookie(); }, []); // eslint-disable-line

  // Reload from cookie when window regains focus (user switching tabs)
  useEffect(() => {
    window.addEventListener("focus", loadFromCookie);
    return () => window.removeEventListener("focus", loadFromCookie);
  }, [loadFromCookie]);

  // Persist on state change
  useEffect(() => { persistCookie(state); }, [state, persistCookie]);

  // ── Open / Close ──────────────────────────────────────────────────────────
  const openDraftPrompt = useCallback(() => {
    setState((prev) => ({ ...prev, open: true }));
    setOpen(true);
  }, []);

  const closeDraftPrompt = useCallback(() => {
    setState((prev) => ({ ...prev, open: false }));
    setOpen(false);
  }, []);

  // ── Clear ─────────────────────────────────────────────────────────────────
  const clear = useCallback(() => {
    const d = nowDefaults();
    setState({
      ...defaultState(),
      userdate:   d.userdate,
      userhour:   d.userhour,
      userminute: d.userminute,
    });
  }, []);

  // ── Item management ───────────────────────────────────────────────────────
  const itemAdd = useCallback((type, id, url, caption, role = "user") => {
    setState((prev) => {
      let items = [...(prev.items || [])];

      // if adding a platform reel and one already exists, confirm replace
      if (prev.type === "reel") {
        const platformReel = items.find((i) => i.role === "platform");
        if (platformReel) {
          if (!window.confirm("This will replace the reel in the post builder. Do you want to continue?")) {
            return prev;
          }
          items = items.filter((i) => i.role !== "platform");
        }
      }

      const exists = items.some((i) => i.type === type && i.id === id);
      if (!exists && items.length < 10) {
        items = [...items, { type, id, url, role }];
      }

      let content = prev.content;
      if (caption !== undefined) {
        const captionEl = document.querySelector(`.${type}-${id} .caption-content`);
        if (captionEl) content = captionEl.textContent;
      }

      return { ...prev, items, content };
    });
    setOpen(true);
  }, []);

  const itemRemove = useCallback((type, id) => {
    setState((prev) => ({
      ...prev,
      items: prev.items.filter((i) => !(i.type === type && i.id === id)),
    }));
  }, []);

  const itemsReorder = useCallback((newItems) => {
    setState((prev) => ({ ...prev, items: newItems }));
  }, []);

  // ── Caption ───────────────────────────────────────────────────────────────
  const captionAdd = useCallback((idcaption, content) => {
    setState((prev) => ({ ...prev, idcaption, content }));
    setOpen(true);
  }, []);

  // ── Save (add / edit) ─────────────────────────────────────────────────────
  const save = useCallback(async (toPost, scheduled) => {
    if (!state.content.trim()) {
      alert("Add a caption to your post.");
      return false;
    }

    const isScheduled = state.scheduled || scheduled;
    const userdatetime = buildUserDatetime();

    if (isScheduled && !state.userdate) {
      alert("Add a valid date to schedule your post.");
      return false;
    }

    let serverdatetime = "";
    const parsed = new Date(userdatetime);
    if (!isNaN(parsed)) {
      serverdatetime = parsed.toISOString().substring(0, 16);
    }

    const action = state.iddraft ? "edit" : "add";
    setSaving(true);

    try {
      const fd = new FormData();
      if (state.iddraft) fd.append("iddraft", state.iddraft);
      fd.append("idimage",       state.idimage);
      fd.append("idimagecover",  state.idimagecover);
      fd.append("idreel",        state.idreel);
      fd.append("type",          state.type);
      fd.append("idcaption",     state.idcaption);
      fd.append("content",       state.content);
      fd.append("facebook",      state.facebook);
      fd.append("instagram",     state.instagram);
      fd.append("linkedin",      state.linkedin);
      fd.append("tiktok",        state.tiktok);
      fd.append("serverdatetime", serverdatetime);
      fd.append("userdatetime",  userdatetime);
      fd.append("scheduled",     isScheduled);
      fd.append("posted",        toPost);
      (state.items || []).forEach((item, index) => {
          fd.append(`items[${index}][type]`, item.type || "");
          fd.append(`items[${index}][id]`, item.id || "");
          fd.append(`items[${index}][url]`, item.url || "");
          fd.append(`items[${index}][role]`, item.role || "");
        });

        const res = await fetch(`${API.drafts}?${action}`, {
          method: "POST",
          credentials: "include",
          body: fd,
        });

        const iddraft = await res.json();
        patch({ iddraft: String(iddraft) });

        // Notify calendar if active
        window._calendar?.refresh();
        if(toPost){
          window._alert('success', '✅ Scheduled to Post');
        }else{
          window._alert('success', '✅ Draft Saved');
        }
        

      closeDraftPrompt();
      clear();
      return true;
    } catch (err) {
      console.error("DraftPrompt save:", err);
      return false;
    } finally {
      setSaving(false);
    }
  }, [state, buildUserDatetime, patch, closeDraftPrompt, clear]);

  // ── Load existing draft into DraftPrompt ──────────────────────────────────
  const loadDraft = useCallback(async (iddraft, reuse = false) => {
    try {
      const fd = new FormData();
      fd.append("iddraft", iddraft);
      const res = await fetch(`${API.drafts}?load`, {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      const draft = await res.json();

      setState((prev) => ({
        ...prev,
        iddraft:        reuse ? "" : draft.iddraft,
        items:          draft.items || [],
        idimage:        draft.idimage || "",
        idreel:         draft.idreel || "",
        reel_image:     draft.reelImage || "",
        reel_file:      draft.reelFile || "",
        reel_filewithmusic: draft.reelFileWithMusic || "",
        reel_url:       draft.reelUrl || "",
        reel_music:     draft.reelMusic || "",
        idcaption:      draft.idcaption || "",
        content:        draft.content || "",
        type:           draft.type || "",
        facebook:       !!draft.facebook,
        instagram:      !!draft.instagram,
        linkedin:       !!draft.linkedin,
        tiktok:         !!draft.tiktok,
        scheduled:      !!draft.scheduled,
        userdate:       draft.date || prev.userdate,
        userhour:       draft.time ? draft.time.slice(0, 2) : prev.userhour,
        userminute:     draft.time ? draft.time.slice(3, 5) : prev.userminute,
      }));

      setOpen(true);
    } catch (err) {
      console.error("DraftPrompt loadDraft:", err);
    }
  }, []);

  // ── Window exposure ───────────────────────────────────────────────────────
  useEffect(() => {
    window._draft_prompt_item_add    = (type, id, url, caption, role) => itemAdd(type, id, url, caption, role);
    window._draft_prompt_item_remove = (type, id) => itemRemove(type, id);
    window._draft_prompt_clear       = () => clear();
    window._draft_prompt_caption_add = (idcaption, content) => captionAdd(idcaption, content);
    window._draft_edit               = (iddraft, reuse, _preloader) => loadDraft(iddraft, reuse);
    window._draft_prompt_open        = () => openDraftPrompt();

    return () => {
      delete window._draft_prompt_item_add;
      delete window._draft_prompt_item_remove;
      delete window._draft_prompt_clear;
      delete window._draft_prompt_caption_add;
      delete window._draft_edit;
      delete window._draft_prompt_open;
    };
  }, [itemAdd, itemRemove, clear, captionAdd, loadDraft, openDraftPrompt]);

  return (
    <DraftPromptContext.Provider value={{
      state, patch, open, openDraftPrompt, closeDraftPrompt,
      clear, itemAdd, itemRemove, itemsReorder,
      captionAdd, save, loadDraft, saving,
      buildUserDatetime,
    }}>
      {children}
    </DraftPromptContext.Provider>
  );
}

export function useDraftPrompt() {
  const ctx = useContext(DraftPromptContext);
  if (!ctx) throw new Error("useDraftPrompt must be used inside <DraftPromptProvider>");
  return ctx;
}